import Icon from '@mui/material/Icon'
import Menu from '@mui/material/Menu'
import { styled } from '@mui/material/styles'
import React from 'react'

import Button from '@/app/component/atom/button'
import SearchSelectFilter from '@/app/component/atom/filters/search-select'
import SingleChoice from '@/app/component/atom/filters/single-choice'
import { useGetCampaignOptions } from '@/app/module/campaigns/hooks/use-get-campaign-options'
import type { RecordingsDateRange } from '@/app/module/reports/call-recordings-query'
import { useGetUsers } from '@/app/module/user/hooks/use-get-users'

import CallRecordingsDateFilter from './call-recordings-date-filter'

type Props = {
  campid?: number
  dates?: RecordingsDateRange
  disabled?: boolean
  userid?: number
  onChange: (q: { campid?: number; dates?: RecordingsDateRange; userid?: number }) => void
}

const CallRecordingsFilters: React.FC<Props> = ({ campid, dates, disabled, userid, onChange }) => {
  const { data: usersData } = useGetUsers()

  const { data: campaignOptions, isFetching: campaignsLoading } = useGetCampaignOptions({
    archived: true,
    launched: true,
    templates: 'cati',
  })

  const [userAnchor, setUserAnchor] = React.useState<null | HTMLElement>(null)
  const userOptions = React.useMemo(
    () =>
      (usersData?.users || []).map((u) => ({
        label: u.username,
        value: String(u.id),
      })),
    [usersData],
  )

  return (
    <Row>
      <FieldWrapper>
        <SearchSelectFilter
          label="Campaign"
          name="recordings-campaign"
          value={campid ? String(campid) : undefined}
          options={campaignOptions.map((c: any) => ({ label: c.label, value: String(c.value) }))}
          onChange={(val) => onChange({ campid: val ? Number(val) : undefined, userid })}
          disabled={disabled || campaignsLoading}
          searchPlaceholder="Search campaigns"
        />
      </FieldWrapper>
      <FieldWrapper>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={(e) => setUserAnchor(e.currentTarget)}
          startIcon={<Icon>filter_list</Icon>}
          endIcon={<Icon>arrow_drop_down</Icon>}
          sx={{ textTransform: 'none' }}
        >
          User
        </Button>
        <Menu anchorEl={userAnchor} open={!!userAnchor} onClose={() => setUserAnchor(null)}>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <SingleChoice
              name="recordings-user"
              options={[{ label: 'Any', value: '' }, ...userOptions]}
              value={userid ? String(userid) : ''}
              onChange={(v) => {
                onChange({ campid, userid: v ? Number(v) : undefined })
                setUserAnchor(null)
              }}
            />
          </div>
        </Menu>
      </FieldWrapper>
      <CallRecordingsDateFilter
        disabled={disabled}
        value={dates}
        onChange={(nextDates) => onChange({ dates: nextDates })}
      />
    </Row>
  )
}

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}))

const FieldWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
})

export default CallRecordingsFilters
